/**
 * Image Cache Utility
 * 
 * Client-side caching for profile and business images to reduce API calls
 * Uses localStorage with TTL (Time To Live) for automatic cache invalidation
 */

const CACHE_PREFIX = 'img_cache_';
const PROFILE_IMAGE_KEY = `${CACHE_PREFIX}profile`;
const BUSINESS_IMAGE_KEY = `${CACHE_PREFIX}business`;
const CACHE_VERSION_KEY = `${CACHE_PREFIX}version`;
const CURRENT_CACHE_VERSION = '1.0';

// Cache duration: 24 hours (in milliseconds)
const CACHE_TTL = 24 * 60 * 60 * 1000;

interface CachedImage {
  url: string;
  timestamp: number;
  version: string;
}

/**
 * Initialize cache version (clear old cache if version changed)
 */
const initializeCacheVersion = (): void => {
  try {
    const storedVersion = localStorage.getItem(CACHE_VERSION_KEY);
    if (storedVersion !== CURRENT_CACHE_VERSION) {
      console.log('📦 Image cache version mismatch, clearing old cache...');
      clearAllImageCache();
      localStorage.setItem(CACHE_VERSION_KEY, CURRENT_CACHE_VERSION);
    }
  } catch (error) {
    console.error('Failed to initialize cache version:', error);
  }
};

/**
 * Check if cached data is still valid (not expired)
 */
const isCacheValid = (cachedData: CachedImage): boolean => {
  const now = Date.now();
  const age = now - cachedData.timestamp;
  return age < CACHE_TTL && cachedData.version === CURRENT_CACHE_VERSION;
};

/**
 * Get cached image URL with validation
 */
const getCachedImage = (key: string): string | null => {
  try {
    const cachedJson = localStorage.getItem(key);
    if (!cachedJson) return null;

    const cached: CachedImage = JSON.parse(cachedJson);
    
    // Validate cache
    if (!isCacheValid(cached)) {
      console.log(`🗑️ Cache expired for ${key}, removing...`);
      localStorage.removeItem(key);
      return null;
    }

    console.log(`✅ Using cached image for ${key}`);
    return cached.url;
  } catch (error) {
    console.error(`Failed to retrieve cached image for ${key}:`, error);
    localStorage.removeItem(key);
    return null;
  }
};

/**
 * Set cached image URL with timestamp
 */
const setCachedImage = (key: string, url: string): void => {
  try {
    if (!url || url.trim() === '') {
      console.warn(`⚠️ Attempted to cache empty URL for ${key}`);
      return;
    }

    const cachedData: CachedImage = {
      url: url.trim(),
      timestamp: Date.now(),
      version: CURRENT_CACHE_VERSION,
    };

    localStorage.setItem(key, JSON.stringify(cachedData));
    console.log(`💾 Cached image for ${key}`, { url: url.substring(0, 50) + '...' });
  } catch (error) {
    console.error(`Failed to cache image for ${key}:`, error);
  }
};

/**
 * Remove cached image
 */
const removeCachedImage = (key: string): void => {
  try {
    localStorage.removeItem(key);
    console.log(`🗑️ Removed cache for ${key}`);
  } catch (error) {
    console.error(`Failed to remove cached image for ${key}:`, error);
  }
};

// ==================== Profile Image ====================

/**
 * Get cached profile image URL
 */
export const getCachedProfileImage = (): string | null => {
  initializeCacheVersion();
  return getCachedImage(PROFILE_IMAGE_KEY);
};

/**
 * Set cached profile image URL
 */
export const setCachedProfileImage = (url: string): void => {
  initializeCacheVersion();
  setCachedImage(PROFILE_IMAGE_KEY, url);
};

/**
 * Remove cached profile image
 */
export const removeCachedProfileImage = (): void => {
  removeCachedImage(PROFILE_IMAGE_KEY);
};

// ==================== Business Image ====================

/**
 * Get cached business image URL
 */
export const getCachedBusinessImage = (): string | null => {
  initializeCacheVersion();
  return getCachedImage(BUSINESS_IMAGE_KEY);
};

/**
 * Set cached business image URL
 */
export const setCachedBusinessImage = (url: string): void => {
  initializeCacheVersion();
  setCachedImage(BUSINESS_IMAGE_KEY, url);
};

/**
 * Remove cached business image
 */
export const removeCachedBusinessImage = (): void => {
  removeCachedImage(BUSINESS_IMAGE_KEY);
};

// ==================== Utility Functions ====================

/**
 * Clear all image cache
 */
export const clearAllImageCache = (): void => {
  try {
    removeCachedProfileImage();
    removeCachedBusinessImage();
    console.log('🗑️ Cleared all image cache');
  } catch (error) {
    console.error('Failed to clear all image cache:', error);
  }
};

/**
 * Get cache statistics (for debugging)
 */
export const getImageCacheStats = (): {
  profileImage: { cached: boolean; age?: number };
  businessImage: { cached: boolean; age?: number };
} => {
  const stats = {
    profileImage: { cached: false, age: undefined as number | undefined },
    businessImage: { cached: false, age: undefined as number | undefined },
  };

  try {
    const profileJson = localStorage.getItem(PROFILE_IMAGE_KEY);
    if (profileJson) {
      const cached: CachedImage = JSON.parse(profileJson);
      stats.profileImage.cached = isCacheValid(cached);
      stats.profileImage.age = Date.now() - cached.timestamp;
    }

    const businessJson = localStorage.getItem(BUSINESS_IMAGE_KEY);
    if (businessJson) {
      const cached: CachedImage = JSON.parse(businessJson);
      stats.businessImage.cached = isCacheValid(cached);
      stats.businessImage.age = Date.now() - cached.timestamp;
    }
  } catch (error) {
    console.error('Failed to get cache stats:', error);
  }

  return stats;
};

/**
 * Force refresh cache (clear and require re-fetch)
 */
export const forceRefreshImageCache = (): void => {
  clearAllImageCache();
  console.log('🔄 Forced image cache refresh');
};

